import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useGetPatientDetailQuery } from "../features/data/patientApiSlice";
import { createStyleSheet } from "../utils/styles";
import { FeedbackFormData, FeedbackPriority } from "../features/api/logging";
import { setFeedbackDialogInfo } from "../features/appState/uxSlice";
import { useDispatch } from "react-redux";

interface FeedbackDialogProps {
  open: boolean;
  onSubmit: (data: FeedbackFormData) => void;
  patientUid: string;
}

const FeedbackDialog = ({
  open,
  onSubmit,
  patientUid,
}: FeedbackDialogProps) => {
  const dispatch = useDispatch();

  const { t } = useTranslation("FeedbackDialog");
  const { data: patient } = useGetPatientDetailQuery(
    { uid: patientUid },
    { skip: !patientUid.trim() }
  );
  const feedbackCategories = useMemo(
    () => Object.keys(t("feedbackCategories", { returnObjects: true })),
    [t]
  );

  const [formData, setFormData] = useState<FeedbackFormData>({
    feedbackCategories: [],
    isRelatedToPortal: false,
    portalId: "",
    description: "",
    priority: FeedbackPriority.GeneralFeedback,
  });

  const [showErrors, setShowErrors] = useState(false);
  const [isRelatedToPortalValue, setIsRelatedToPortalValue] = useState(false);
  const handleFormDataChange = useCallback(
    <K extends keyof FeedbackFormData>(
      field: K,
      value: FeedbackFormData[K]
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleCategoryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFormData((prev) => ({
        ...prev,
        feedbackCategories: prev.feedbackCategories.includes(value)
          ? prev.feedbackCategories.filter((cat) => cat !== value)
          : [...prev.feedbackCategories, value],
      }));
    },
    []
  );

  const onClose = useCallback(() => {
    setFormData({
      feedbackCategories: [],
      isRelatedToPortal: false,
      portalId: "",
      description: "",
      priority: FeedbackPriority.GeneralFeedback,
    });
    setIsRelatedToPortalValue(false);
    dispatch(setFeedbackDialogInfo(false));
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    setShowErrors(true);
    const { feedbackCategories, isRelatedToPortal, portalId, description } =
      formData;

    const hasErrors =
      feedbackCategories.length === 0 ||
      !description.trim() ||
      (isRelatedToPortal === true && !portalId?.trim());

    if (hasErrors) return;

    onSubmit({
      ...formData,
      portalId: isRelatedToPortal === true ? portalId : undefined,
    });
    setFormData({
      feedbackCategories: [],
      isRelatedToPortal: false,
      portalId: "",
      description: "",
      priority: FeedbackPriority.GeneralFeedback,
    });
    setIsRelatedToPortalValue(false);
    setShowErrors(false);
    onClose();
  }, [formData, onClose, onSubmit]);

  useEffect(() => {
    if (open && patient?.referral_id) {
      setFormData((prev) => ({
        ...prev,
        portalId: patient.referral_id ?? "",
      }));
    }
  }, [open, patient]);

  const {
    feedbackCategories: selectedCategories,
    isRelatedToPortal,
    portalId,
    description,
    priority,
  } = formData;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6" sx={styles.titleText}>
          {t("feedbackInfo")}
        </Typography>
        <Typography variant="body2" sx={styles.subtitleText}>
          {t("feedbackInfo_2")}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <FormControl
          required
          fullWidth
          sx={styles.formControl}
          error={selectedCategories.length === 0 && showErrors}
        >
          <FormLabel>{t("CategoryText")}</FormLabel>
          <FormGroup>
            {feedbackCategories.map((category) => (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    checked={selectedCategories.includes(category)}
                    onChange={handleCategoryChange}
                    value={category}
                  />
                }
                label={t(`feedbackCategories.${category}`)}
              />
            ))}
          </FormGroup>
          {selectedCategories.length === 0 && showErrors && (
            <FormHelperText>{t("categoryErrorText")}</FormHelperText>
          )}
        </FormControl>
        <FormControl required sx={styles.radioGroup}>
          <FormLabel>{t("clinicalSpecificPortalText")}</FormLabel>
          <RadioGroup
            row
            value={isRelatedToPortalValue}
            onChange={(e) => {
              const value = e.target.value === "true";
              setIsRelatedToPortalValue(value);
              handleFormDataChange("isRelatedToPortal", value);
            }}
          >
            <FormControlLabel
              value="true"
              control={<Radio />}
              label={t("common:yes")}
            />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label={t("common:no")}
            />
          </RadioGroup>
        </FormControl>
        {isRelatedToPortal === true && (
          <TextField
            label={t("clinicalPortalID")}
            variant="outlined"
            fullWidth
            required
            sx={styles.formControl}
            value={portalId}
            onChange={(e) => handleFormDataChange("portalId", e.target.value)}
            error={showErrors && !portalId?.trim()}
            helperText={
              showErrors && !portalId?.trim()
                ? t("clinicalPortalIDErrorText")
                : ""
            }
          />
        )}
        <TextField
          label={t("descriptionText")}
          variant="outlined"
          multiline
          fullWidth
          required
          rows={4}
          sx={styles.descriptionField}
          value={description}
          onChange={(e) => {
            const val = e.target.value;
            if (val.length <= 5000) handleFormDataChange("description", val);
          }}
          error={showErrors && !description.trim()}
          helperText={
            showErrors && !description.trim()
              ? t("descriptionErrorText")
              : t("descriptionCount", { count: description.length })
          }
        />
        <FormControl sx={styles.radioGroup}>
          <FormLabel>{t("priorityLabel")}</FormLabel>
          <RadioGroup
            row
            value={priority}
            onChange={(e) =>
              handleFormDataChange(
                "priority",
                e.target.value as FeedbackFormData["priority"]
              )
            }
          >
            <FormControlLabel
              value={FeedbackPriority.Urgent}
              control={<Radio />}
              label={t("priorityText_1")}
            />
            <FormControlLabel
              value={FeedbackPriority.GeneralFeedback}
              control={<Radio />}
              label={t("priorityText_2")}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={onClose}>{t("common:cancel")}</Button>
        <Button onClick={handleSubmit}>{t("submitText")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;

const styles = createStyleSheet({
  dialogTitle: {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
  },
  subtitleText: {
    color: "text.secondary",
  },
  titleText: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  formControl: {
    marginTop: 2,
  },
  radioGroup: {
    marginTop: 3,
  },
  descriptionField: {
    marginTop: 3,
  },
  actions: {
    paddingX: 3,
    paddingY: 2,
  },
});
